// Standalone availability grid for the profile page.
// Uses event delegation on the grid container for click handling
// since DOMQL lifecycle hooks don't fire on deeply nested childExtends cells.
//
// Moved from workspace/components/ProfileAvailabilityGrid.js (2026-08-15,
// MemberProfile extraction — tickets/opus.md) so it stays reachable from
// every host MemberProfile renders in (workspace, canvas) — MemberProfile
// bare-keys this component (`ProfileAvailabilityGrid: {}`), and DOMQL's
// key-based auto-extend needs it resolvable from wherever MemberProfile
// itself resolves from.
//
// canAdmin/canEditProfile here are this package's memberProfileAccess.js
// (not workspace's roleHelpers.js) — these two helpers are called from
// plain JS closures (_currentUserIsAdmin/_canEditAvailability), not DOMQL
// prop factories, so there's no `el` to route through el.call the way
// MemberProfile.js does for the same functions. The only behavior this
// trades away is roleHelpers.js's local team-permission UNION fallback
// (same documented, already-shipped tradeoff as appAccess.js's
// isOwnerOrAdmin/canApp) — a UI-only cursor/click-affordance gate; the
// server independently enforces the real write authorization.
import { findMe } from '../functions/peopleUtils.js'
import { canAdmin, canEditProfile } from '../functions/memberProfileAccess.js'

// KEYS, not day names — the render site (DayLabel below) resolves them.
// Duplicated under `account.*` rather than read from `calendar.day.*`: a
// surface never reaches into another namespace's keys, and the account path
// does not load the calendar bundle (docs/I18N-CONVENTION.md §5).
const DAYS = [
  'account.availability.day.mon',
  'account.availability.day.tue',
  'account.availability.day.wed',
  'account.availability.day.thu',
  'account.availability.day.fri',
  'account.availability.day.sat',
  'account.availability.day.sun'
]

const _hhmmToMin = (s) => {
  if (!s) return 0
  const [h, m] = s.split(':').map(Number)
  return h * 60 + m
}

const _isAvailableDefault = (user, hour) => {
  const wh = user?.workingHours
  if (!wh) return false
  const minute = hour * 60
  const start = _hhmmToMin(wh.start)
  const end = _hhmmToMin(wh.end)
  return start < end ? minute >= start && minute < end : minute >= start || minute < end
}

const _isAvailableAt = (user, day, hour) => {
  if (!user) return false
  const grid = user.availabilityGrid
  if (grid && grid[day] && typeof grid[day][hour] === 'boolean') {
    return grid[day][hour]
  }
  return _isAvailableDefault(user, hour)
}

// Every prop factory below calls `findMe` directly — a former
// `const _me = findMe` alias sat here, which resolved fine for this file's
// own render pass but couldn't be chased back to the real `findMe` export
// when frank re-extracted any of these nested prop factories as a
// standalone unit (`unresolvable-identifier` on `_me`, same class as
// MemberProfile.js — see tickets/fable.md "decompose workspace-shell's
// 507 defects by owning package").

// CU-10 §90: roles/isAdmin removed from person object. Use canAdmin() which
// unions JWT perm check (admin.pages.read) with legacy role fallback.
const _currentUserIsAdmin = (root) => {
  if (!root) return false
  // canAdmin uses Phase E JWT claim as primary path; falls back to userIsAdmin
  // which reads root.roles.byUser (populated from server member-roles endpoint).
  return canAdmin({ root })
}

// A member sets their OWN availability from their profile; admins can still
// edit anyone's. canEditProfile = self OR profile.edit_others OR superadmin.
const _canEditAvailability = (state) => {
  const root = state?.root
  if (!root) return false
  return canEditProfile({ root }, findMe(state)) || _currentUserIsAdmin(root)
}

export const ProfileAvailabilityCell = {
  tag: 'button',
  border: 'none',
  padding: '0',
  height: 'A',
  minHeight: 'A',
  background: (el, s) => {
    const u = findMe(el.state)
    return _isAvailableAt(u, s.day, s.h) ? 'green' : 'red.15'
  },
  cursor: (el) => (_canEditAvailability(el.state) ? 'pointer' : 'not-allowed'),
  // Cell fill IS the data (green/red-tint) — a neutral 'hover' wash would
  // mute the signal, and it's too subtle to read per-cell in a 168-cell
  // grid. Hued-surface exception (§4.3): brightness step, not opacity.
  ':hover': { filter: 'brightness(1.12)' }
}

export const ProfileAvailabilityGrid = {
  display: 'grid',
  gridTemplateColumns: 'auto repeat(24, minmax(32px, 1fr))',
  gridTemplateRows: 'auto repeat(7, auto)',
  gap: '1px',
  background: 'line',
  padding: '1px',
  round: 'radiusControl',
  overflowX: 'auto',
  overflowY: 'hidden',
  userSelect: 'none',

  // Tooltip binding
  attr: {
    title: (el, s) => {
      const u = findMe(s)
      if (!s.root?.currentUserEmail || !u)
        return el.call('polyglot', 'account.member.availability.readOnly')

      if (s.root.currentUserEmail === u.email)
        return el.call('polyglot', 'account.member.availability.toggle')

      if (_currentUserIsAdmin(s.root))
        return el.call('polyglot', 'account.member.availability.toggleAdmin')

      return el.call('polyglot', 'account.member.availability.readOnly')
    }
  },

  // Event delegation: ONE declared click handler on the grid container.
  // FA503: was `el.node.addEventListener('click', …)` armed from `onCreate`
  // with no removal — every re-mount of the profile added another listener to
  // the same node, and the shift-range anchor lived in a closure that only
  // the listener could reach. Declared, DOMQL registers it once and removes it
  // in dispose(); the anchor rides the element itself.
  onClick: (e, el, s) => {
    const btn = e.target.closest('button')
    if (!btn) return
    const root = s.root || el.state?.root
    // Self OR admin — a member sets their OWN availability on their profile.
    if (!_canEditAvailability(el.state)) return

    // Find day/hour from the button's data attributes
    const day = parseInt(btn.dataset.day)
    const hour = parseInt(btn.dataset.hour)
    if (isNaN(day) || isNaN(hour)) return

    const user = findMe(el.state)
    if (!user) return

    const grid = user.availabilityGrid
      ? user.availabilityGrid.map((row) => row.slice())
      : Array.from({ length: 7 }, () =>
          Array.from({ length: 24 }, (_, h) => _isAvailableDefault(user, h))
        )

    const cellsToUpdate = []

    const lastClicked = el._availLastClicked || null
    if (e.shiftKey && lastClicked !== null) {
      const lastIndex = lastClicked.day * 24 + lastClicked.hour
      const currentIndex = day * 24 + hour

      const minIndex = Math.min(lastIndex, currentIndex)
      const maxIndex = Math.max(lastIndex, currentIndex)

      for (let ii = minIndex; ii <= maxIndex; ii++) {
        const d = Math.floor(ii / 24)
        const h = ii % 24
        grid[d][h] = lastClicked.targetState
        cellsToUpdate.push({ d, h, state: lastClicked.targetState })
      }
    } else {
      const targetState = !grid[day][hour]
      grid[day][hour] = targetState
      cellsToUpdate.push({ d: day, h: hour, state: targetState })
      el._availLastClicked = { day, hour, targetState }
    }

    const next = (root.people || []).map((p) =>
      p.email === user.email ? { ...p, availabilityGrid: grid } : p
    )
    root.update({ people: next })
    el.call('saveAvailabilityGrid', root, user, grid)

    cellsToUpdate.forEach(({ d, h, state }) => {
      const targetBtn = el.node.querySelector(`button[data-day="${d}"][data-hour="${h}"]`)
      if (targetBtn) {
        targetBtn.style.background = state
          ? 'var(--color-green)'
          : 'color-mix(in srgb, var(--color-red) 15%, transparent)'
      }
    })
  },

  Corner: { background: 'modal', padding: 'Y' },

  HourHeaders: {
    display: 'contents',
    childExtends: {
      tag: 'span',
      background: 'modal',
      fontSize: 'Y2',
      color: 'caption',
      fontFamily: 'monospace',
      textAlign: 'center',
      padding: 'Y 0',
      text: (el, s) => String(s.h).padStart(2, '0')
    },
    childrenAs: 'state',
    children: () => Array.from({ length: 24 }, (_, h) => ({ h }))
  },

  DayRows: {
    display: 'contents',
    childExtends: {
      display: 'contents',
      Label: {
        tag: 'span',
        background: 'modal',
        fontSize: 'Y2',
        color: 'caption',
        fontWeight: '700',
        padding: 'Y Z',
        align: 'center right',
        // KEY-IN-STATE contract: `DAYS` carries the keys, this site resolves
        // them, so the strip re-renders on a language switch.
        text: (el, s) => el.call('polyglot', s.label || '')
      },
      Cells: {
        display: 'contents',
        childExtends: {
          tag: 'button',
          border: 'none',
          padding: '0',
          height: 'A',
          minHeight: 'A',
          background: (el, s) => {
            const u = findMe(el.state)
            return _isAvailableAt(u, s.day, s.h) ? 'green' : 'red.15'
          },
          cursor: (el) => (_canEditAvailability(el.state) ? 'pointer' : 'not-allowed'),
          // Hued-surface exception (§4.3) — see ProfileAvailabilityCell above.
          ':hover': { filter: 'brightness(1.12)' },
          // Store day/hour as data attributes for the delegated click handler
          attr: {
            'data-day': (el, s) => s.day,
            'data-hour': (el, s) => s.h
          }
        },
        childrenAs: 'state',
        children: (el, s) => Array.from({ length: 24 }, (_, h) => ({ day: s.day, h }))
      }
    },
    childrenAs: 'state',
    children: () => DAYS.map((label, day) => ({ day, label }))
  }
}
